/**
 * Tableau de bord organisateur — port de admin.index.tsx, branché :
 * ticker sur /api/paiements/commissions (vraies commissions), métriques sur
 * /api/organisateur/evenements/:id/metriques. Les encarts « argent laissé sur
 * la table » et « poisson important » attendent l'enrichissement IA : ils
 * s'affichent comme aperçus explicitement marqués, jamais comme du réel.
 */
import { useEffect, useState } from 'react';
import { appelerApi } from '../../reseau.ts';
import { Badge, Card, Metric, PageTitle, SectionLabel, eur } from '../ui.tsx';
import type { EvenementAdmin } from '../EspaceAdmin.tsx';

type Commissions = {
  total: number;
  sponsors: number;
  parrainage: number;
  en_attente: number;
  aujourdhui: number;
};

type Metriques = {
  participants: string;
  profils_completes: string;
  taux_profils_completes: string | null;
  rdv_confirmes: string;
  rdv_par_participant: string | null;
  connexions: string;
  taux_optin_diagnostic: number;
  presents_maintenant: number;
  a_rejoint: number;
};

function Ticker({ commissions }: { commissions: Commissions }) {
  const [total, setTotal] = useState(0);
  useEffect(() => {
    const cible = commissions.total;
    if (cible === 0) {
      setTotal(0);
      return;
    }
    const debut = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - debut) / 1200);
      setTotal(cible * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [commissions.total]);

  return (
    <div className="rounded-[10px] bg-charbon p-6 sm:p-8">
      <SectionLabel>Total gagné en commissions</SectionLabel>
      <div className="mt-3 flex flex-wrap items-baseline gap-4">
        <p className="text-match-gradient display-title text-[40px]">{eur(total)}</p>
        <p className="text-[14px] text-fumee">
          {commissions.aujourdhui > 0 ? `+${eur(commissions.aujourdhui)} aujourd'hui` : "rien encore aujourd'hui"}
        </p>
      </div>
      <div className="mt-7 grid gap-5 border-t border-graphite pt-5 sm:grid-cols-3">
        {[
          { l: 'Commissions sponsors', v: eur(commissions.sponsors) },
          { l: 'Commissions parrainage', v: eur(commissions.parrainage) },
          { l: 'En attente de versement', v: eur(commissions.en_attente) },
        ].map((c) => (
          <div key={c.l}>
            <SectionLabel>{c.l}</SectionLabel>
            <p className="mt-2 text-[20px] font-light text-blanc">{c.v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TableauDeBord({ evenements }: { evenements: EvenementAdmin[] }) {
  const [commissions, setCommissions] = useState<Commissions | null>(null);
  const [metriques, setMetriques] = useState<Metriques | null>(null);
  const evenement = evenements[0];

  useEffect(() => {
    void appelerApi<Commissions & { lignes: unknown[] }>('/api/paiements/commissions').then((r) => {
      if (r.donnees) setCommissions(r.donnees);
    });
    if (evenement) {
      void appelerApi<{ metriques: Metriques }>(
        `/api/organisateur/evenements/${evenement.id}/metriques`,
      ).then((r) => {
        if (r.donnees) setMetriques(r.donnees.metriques);
      });
    }
  }, [evenement?.id]);

  return (
    <>
      <PageTitle
        title="Tableau de bord"
        subtitle={
          evenement
            ? `${evenement.nom}${evenement.lieu_ville ? ` — ${evenement.lieu_ville}` : ''}.`
            : 'Aucun événement pour votre organisation pour l’instant.'
        }
      />

      {commissions && <Ticker commissions={commissions} />}

      {metriques && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Metric label="Inscrits" value={metriques.participants} detail={`${metriques.a_rejoint} ont rejoint`} />
          <Metric
            label="Profils complétés"
            value={`${metriques.taux_profils_completes ?? 0} %`}
            detail="Cible > 70 %"
            ok={Number(metriques.taux_profils_completes) >= 70}
          />
          <Metric
            label="Rendez-vous / participant"
            value={metriques.rdv_par_participant ?? '0'}
            detail="Cible ≥ 2"
            ok={Number(metriques.rdv_par_participant) >= 2}
          />
          <Metric
            label="Opt-in diagnostic IA"
            value={`${metriques.taux_optin_diagnostic} %`}
            detail="Cible > 25 %"
            ok={metriques.taux_optin_diagnostic >= 25}
          />
        </div>
      )}

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card>
          <div className="flex items-center justify-between gap-3">
            <SectionLabel>Argent laissé sur la table</SectionLabel>
            <Badge>Bientôt</Badge>
          </div>
          <p className="mt-3 text-[15px] leading-relaxed text-fumee">
            Ce nudge s'activera avec le pipeline sponsors : les sponsors de vos événements passés
            non encore invités au prochain apparaîtront ici.
          </p>
        </Card>
        <Card>
          <div className="flex items-center justify-between gap-3">
            <SectionLabel>Alerte poisson important</SectionLabel>
            <Badge>Bientôt</Badge>
          </div>
          <p className="mt-3 text-[15px] leading-relaxed text-fumee">
            Dès que l'enrichissement IA des entreprises sera branché, les inscriptions à fort
            potentiel (gros CA détecté) déclencheront une alerte ici.
          </p>
        </Card>
      </div>
    </>
  );
}
